import React, { useState, useEffect } from "react";
import { Filter, X } from "lucide-react";
import { Button } from "@/components/Calendar/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/Calendar/ui/popover";
import { Badge } from "@/components/Calendar/ui/badge";
import { Separator } from "@/components/Calendar/ui/separator";
import { ServiceTypeFilter } from "./ServiceTypeFilter";
import { PharmacistFilter } from "./PharmacistFilter";
import { RoomFilter } from "./RoomFilter";
import { BlockOutTypesFilter } from "./BlockOutTypesFilter";
import { PharmaciesFilter } from "./PharmaciesFilter";
import { type Member } from "@/components/Calendar/membersData";

interface ScheduleFiltersProps {
  isDarkMode: boolean;
  selectedServiceTypes: string[];
  onServiceTypeChange: (serviceType: string, checked: boolean) => void;
  onSelectAllServices: (checked: boolean) => void;
  serviceTypes: string[];
  selectedPharmacists: string[];
  onPharmacistChange: (pharmacist: string, checked: boolean) => void;
  onSelectAllPharmacists: (checked: boolean) => void;
  pharmacists: string[]; // Changed from clinicians to pharmacists
  selectedRooms: string[];
  onRoomChange: (room: string, checked: boolean) => void;
  onSelectAllRooms: (checked: boolean) => void;
  roomNames: string[];
  selectedBlockOutTypes: string[];
  onBlockOutTypeChange: (type: string, checked: boolean) => void;
  onSelectAllBlockOutTypes: (checked: boolean) => void;
  selectedPharmacies?: string[];
  onPharmacyChange?: (pharmacyId: string, checked: boolean) => void;
  onSelectAllPharmacies?: (checked: boolean) => void;
  pharmacies?: any[];
}

export function ScheduleFilters({
  isDarkMode,
  selectedServiceTypes,
  onServiceTypeChange,
  onSelectAllServices,
  serviceTypes,
  selectedPharmacists,
  onPharmacistChange,
  onSelectAllPharmacists,
  pharmacists,
  selectedRooms,
  onRoomChange,
  onSelectAllRooms,
  roomNames,
  selectedBlockOutTypes,
  onBlockOutTypeChange,
  onSelectAllBlockOutTypes,
  selectedPharmacies = [],
  onPharmacyChange,
  onSelectAllPharmacies,
  pharmacies = [],
}: ScheduleFiltersProps) {
  const [filterOpen, setFilterOpen] = useState(false);

  // Count active filters
  const getActiveFiltersCount = () => {
    let count = 0;
    if (selectedServiceTypes.length > 0 && selectedServiceTypes.length < serviceTypes.length)
      count++;
    if (
      selectedPharmacists.length > 0 &&
      selectedPharmacists.length < pharmacists.length
    )
      count++;
    if (selectedRooms.length > 0) count++; // Assuming rooms are optional
    if (selectedBlockOutTypes.length > 0) count++;
    if (selectedPharmacies.length > 0) count++;
    return count;
  };

  const activeFiltersCount = getActiveFiltersCount();

  // Reset all filters
  const handleResetFilters = () => {
    onSelectAllServices(false);
    onSelectAllPharmacists(false);
    onSelectAllRooms(false);
    onSelectAllBlockOutTypes(false);
    if (onSelectAllPharmacies) onSelectAllPharmacies(false);
    setFilterOpen(false);
  };

  return (
    <Popover open={filterOpen} onOpenChange={setFilterOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`relative ${
            isDarkMode
              ? "bg-gray-800 border-gray-700 text-white hover:bg-gray-700"
              : "bg-white border-gray-300 hover:bg-gray-50"
          }`}
        >
          <Filter className="w-4 h-4 mr-2" />
          Filters
          {activeFiltersCount > 0 && (
            <Badge
              className="ml-2 px-1.5 py-0.5 min-w-[20px] h-5 flex items-center justify-center"
              variant="default"
            >
              {activeFiltersCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        collisionPadding={12}
        className={`w-[calc(100vw-1.5rem)] sm:w-[380px] p-4 max-h-[min(600px,var(--radix-popover-content-available-height))] overflow-y-auto overscroll-contain ${
          isDarkMode
            ? "bg-gray-800 border-gray-700"
            : "bg-white border-gray-200"
        }`}
      >
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h3
              className={`text-sm font-semibold ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Filter Schedule
            </h3>
            {activeFiltersCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleResetFilters}
                className={`h-7 text-xs ${
                  isDarkMode
                    ? "text-gray-400 hover:text-white"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <X className="w-3 h-3 mr-1" />
                Reset
              </Button>
            )}
          </div>

          {/* Pharmacies Filter */}
          {onPharmacyChange && onSelectAllPharmacies && (
            <>
              <Separator className={isDarkMode ? "bg-gray-700" : "bg-gray-200"} />
              <PharmaciesFilter
                selectedPharmacies={selectedPharmacies}
                onPharmacyChange={onPharmacyChange}
                onSelectAll={onSelectAllPharmacies}
                isDarkMode={isDarkMode}
                pharmacies={pharmacies}
              />
            </>
          )}

          <Separator className={isDarkMode ? "bg-gray-700" : "bg-gray-200"} />

          {/* Service Type Filters */}
          <ServiceTypeFilter
            selectedServiceTypes={selectedServiceTypes}
            onServiceTypeChange={onServiceTypeChange}
            onSelectAll={onSelectAllServices}
            serviceTypes={serviceTypes}
            isDarkMode={isDarkMode}
          />

          <Separator className={isDarkMode ? "bg-gray-700" : "bg-gray-200"} />

          {/* Pharmacist Filters */}
          <PharmacistFilter
            selectedPharmacists={selectedPharmacists}
            onPharmacistChange={onPharmacistChange}
            onSelectAll={onSelectAllPharmacists}
            pharmacists={pharmacists}
            isDarkMode={isDarkMode}
          />

          <Separator className={isDarkMode ? "bg-gray-700" : "bg-gray-200"} />

          {/* Room Filters */}
          <RoomFilter
            selectedRooms={selectedRooms}
            onRoomChange={onRoomChange}
            onSelectAll={onSelectAllRooms}
            roomNames={roomNames}
            isDarkMode={isDarkMode}
          />

          <Separator className={isDarkMode ? "bg-gray-700" : "bg-gray-200"} />

          {/* Block-Out Types Filters */}
          <BlockOutTypesFilter
            selectedBlockOutTypes={selectedBlockOutTypes}
            onBlockOutTypeChange={onBlockOutTypeChange}
            onSelectAll={onSelectAllBlockOutTypes}
            isDarkMode={isDarkMode}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
